import { money } from "./tariff";
import type { PublicOrder, PublicOrderState } from "./types";

/**
 * A public order as a picture: where its money went, and how far it got.
 *
 * The order panel leads with one bar — what was taken, divided into what the
 * charge used and what became of the rest — because "where is my money" is the
 * question every order is opened to answer, and four separate figures make the
 * reader do the subtraction. Pure, so the arithmetic is checked without a DOM.
 *
 * Every segment comes from a figure the server gave. Nothing here infers a
 * refund from a state name: if the server has not said an amount was returned or
 * is owed, the bar does not say so either.
 */

/**
 * - `used` — paid for energy that was delivered (or taken from a hold for it).
 * - `refunded` — returned to the driver.
 * - `owed` — still to be returned; the one segment that needs somebody.
 * - `kept` — unused, but under §11.3's refund floor, so deliberately not returned.
 * - `released` — a hold's unused part, never taken from the card at all.
 * - `uncollected` — energy delivered on a hold whose capture failed: revenue at risk.
 * - `open` — taken or held, and not yet divided because the charge has not closed.
 */
export type FlowKind = "used" | "refunded" | "owed" | "kept" | "released" | "uncollected" | "open";

export interface FlowSegment {
  readonly kind: FlowKind;
  readonly minor: bigint;
  readonly amount: string;
  /** Percentage of the bar, to one decimal. */
  readonly share: number;
}

export interface MoneyFlow {
  readonly totalLabel: "Paid" | "Held";
  readonly total: string;
  readonly segments: readonly FlowSegment[];
  /**
   * Set when more has gone back (or is owed back) than the charge left unused —
   * a hand refund in the merchant portal can do that. The excess came out of the
   * used part, so the bar still sums to what was paid, and this names the excess
   * so the panel can say so rather than quietly redrawing the split.
   */
  readonly overRefunded?: string;
}

/**
 * The order's money, divided. `null` when no money ever moved — an order that
 * expired before payment has nothing to draw, and an empty bar would read as $0.
 */
export function moneyFlow(order: PublicOrder): MoneyFlow | null {
  if (order.settledAt === null) return null;

  const total = BigInt(order.amountMinor);
  const over = order.paymentFlow === "pre_auth" ? 0n : overRefund(order, total);
  const parts =
    order.paymentFlow === "pre_auth" ? holdParts(order, total) : purchaseParts(order, total, over);

  return {
    ...(over > 0n ? { overRefunded: money(String(over), order.currency) } : {}),
    totalLabel: order.paymentFlow === "pre_auth" ? "Held" : "Paid",
    total: money(order.amountMinor, order.currency),
    segments: parts
      .filter(([, minor]) => minor > 0n)
      .map(([kind, minor]) => ({
        kind,
        minor,
        amount: money(String(minor), order.currency),
        share: total === 0n ? 0 : Math.round((Number(minor) / Number(total)) * 1000) / 10,
      })),
  };
}

type Part = readonly [FlowKind, bigint];

/**
 * What a purchase left unused, or `undefined` while it is still running.
 *
 * An order owed a full refund before it ever closed — never plugged in, or the
 * station never started — has no unused figure, but the server's debt says the
 * whole amount is coming back. Anything else without one is still running.
 */
function purchaseUnused(order: PublicOrder, total: bigint): bigint | undefined {
  if (order.unusedMinor !== null) return BigInt(order.unusedMinor);
  return BigInt(order.refundedMinor) + BigInt(order.refundOwedMinor) > 0n ? total : undefined;
}

/** How far refunds (made and owed) exceed what the charge left unused. */
function overRefund(order: PublicOrder, total: bigint): bigint {
  const unused = purchaseUnused(order, total);
  if (unused === undefined) return 0n;
  const excess = BigInt(order.refundedMinor) + BigInt(order.refundOwedMinor) - unused;
  return excess > 0n ? excess : 0n;
}

function purchaseParts(order: PublicOrder, total: bigint, over: bigint): readonly Part[] {
  const refunded = BigInt(order.refundedMinor);
  const owed = BigInt(order.refundOwedMinor);
  const unused = purchaseUnused(order, total);
  if (unused === undefined) return [["open", total]];

  const kept = unused - refunded - owed;
  const used = total - unused - over;
  return [
    ["used", used > 0n ? used : 0n],
    ["refunded", refunded],
    ["owed", owed],
    ["kept", kept > 0n ? kept : 0n],
  ];
}

function holdParts(order: PublicOrder, total: bigint): readonly Part[] {
  if (order.state === "released") return [["released", total]];
  if (order.unusedMinor === null) return [["open", total]];

  const unused = BigInt(order.unusedMinor);
  const used = total - unused;

  if (order.state === "captured") return [["used", used], ["released", unused]];
  if (order.state === "capture_failed") return [["uncollected", used], ["released", unused]];
  // Closed, and the capture has not landed yet. The split is known; the money is
  // still on the card.
  return [["open", total]];
}

/**
 * The states that are still going somewhere on their own, so the panel polls.
 *
 * `expired_unplugged` and `start_failed` are here because they are meant to last
 * milliseconds — `refundOrderInFull` moves through them into `refund_required` in
 * the same call — and an operator who opens one is watching a transition land.
 * `capture_required` is the hold tail's equivalent: the sweeper is collecting it.
 */
const LIVE_STATES: ReadonlySet<PublicOrderState> = new Set<PublicOrderState>([
  "pending",
  "awaiting_plug",
  "starting",
  "charging",
  "stopping",
  "expired_unplugged",
  "start_failed",
  "refund_required",
  "capture_required",
]);

export function isOrderLive(state: PublicOrderState): boolean {
  return LIVE_STATES.has(state);
}

/**
 * The states from which a missing step can still happen. Narrower than
 * `LIVE_STATES`: a `refund_required` order is still moving — towards a refund —
 * but it will never start charging, so its missing steps are skipped, not waiting.
 */
const STILL_CHARGING: ReadonlySet<PublicOrderState> = new Set<PublicOrderState>([
  "pending",
  "awaiting_plug",
  "starting",
  "charging",
  "stopping",
]);

export interface JourneyStep {
  readonly label: string;
  readonly at: string | null;
  /** `waiting`: not reached yet, still can be. `skipped`: the order ended without it. */
  readonly status: "done" | "waiting" | "skipped";
}

/**
 * Created → paid → started → closed, as a row of steps. The step an order stopped
 * at is what tells "never plugged in" from "the station never started" from "ran
 * its budget out" at a glance, before reading a word of the outcome sentence.
 */
export function orderJourney(order: PublicOrder): readonly JourneyStep[] {
  const unreached = STILL_CHARGING.has(order.state) ? "waiting" : "skipped";
  const step = (label: string, at: string | null): JourneyStep => ({
    label,
    at,
    status: at === null ? unreached : "done",
  });

  return [
    step("Created", order.createdAt),
    step(order.paymentFlow === "pre_auth" ? "Held" : "Paid", order.settledAt),
    step("Started", order.startedAt),
    // "Closed", not "Ended": `endedAt` is the order's close, which an order that
    // never started still has, and "Started: never · Ended: 09:06" contradicted itself.
    step("Closed", order.endedAt),
  ];
}
